import { useState } from 'react';
import api from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import LottieAnim from '../components/LottieAnim';

export default function LoginPage({ onLogin }) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', form);
      localStorage.setItem('paper_work_token', data.token);
      localStorage.setItem('paper_work_user', JSON.stringify(data.admin));
      onLogin();
    } catch (err) {
      setError(err.response?.data?.message || '로그인 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center overflow-hidden"
      style={{
        backgroundImage: 'url(/uploads/settings/login-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: isLight ? '#f1f5f9' : '#050709',
      }}
    >

      {/* 오버레이 */}
      <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.38)' }} />

      {/* Ambient orbs (dark mode only) */}
      {!isLight && (
        <>
          <div className="absolute" style={{
            width: '600px', height: '600px', borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(224,0,94,0.2) 0%, transparent 65%)',
            filter: 'blur(80px)', top: '-180px', right: '-150px',
            animation: 'orb1-drift 22s ease-in-out infinite',
          }} />
          <div className="absolute" style={{
            width: '480px', height: '480px', borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 68%)',
            filter: 'blur(88px)', bottom: '-100px', left: '10%',
            animation: 'orb2-drift 28s ease-in-out infinite',
          }} />
          <div className="absolute" style={{
            width: '400px', height: '400px', borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(14,165,233,0.12) 0%, transparent 70%)',
            filter: 'blur(96px)', top: '40%', left: '-120px',
            animation: 'orb3-drift 20s ease-in-out infinite',
          }} />
        </>
      )}

      {/* 로그인 박스 */}
      <div className="relative z-10 w-80 page-enter">
        {/* 로고 + 타이틀 */}
        <div className="text-center mb-8">
          <img
            src="/pinkpaper_logo.png"
            alt="핑크페이퍼"
            style={{ filter: 'brightness(0) invert(1)', animation: 'float-slow 6s ease-in-out infinite' }}
            className="h-8 w-auto mx-auto mb-3"
          />
          <p className="text-base tracking-[0.25em] font-medium" style={{ color: 'rgba(255,255,255,0.92)' }}>
            업무관리시스템
          </p>
        </div>

        <div
          className="rounded-2xl p-8 glass-chrome"
          style={{
            backgroundColor: isLight ? 'rgba(255,255,255,0.94)' : 'rgba(10,12,22,0.82)',
            border: isLight ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.1)',
            boxShadow: isLight ? '0 20px 60px rgba(0,0,0,0.12)' : '0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset',
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="text"
              placeholder="아이디"
              value={form.username}
              onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
              className="w-full rounded-lg px-4 py-2.5 focus:outline-none text-sm transition-all duration-200"
              style={{
                backgroundColor: isLight ? '#f9fafb' : 'rgba(255,255,255,0.05)',
                border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
                color: isLight ? '#111827' : '#ffffff',
              }}
              onFocus={e => { if (!isLight) { e.target.style.borderColor = 'rgba(224,0,94,0.5)'; e.target.style.background = 'rgba(255,255,255,0.07)'; } }}
              onBlur={e => { if (!isLight) { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.05)'; } }}
              autoComplete="username"
            />
            <input
              type="password"
              placeholder="비밀번호"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              className="w-full rounded-lg px-4 py-2.5 focus:outline-none text-sm transition-all duration-200"
              style={{
                backgroundColor: isLight ? '#f9fafb' : 'rgba(255,255,255,0.05)',
                border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
                color: isLight ? '#111827' : '#ffffff',
              }}
              onFocus={e => { if (!isLight) { e.target.style.borderColor = 'rgba(224,0,94,0.5)'; e.target.style.background = 'rgba(255,255,255,0.07)'; } }}
              onBlur={e => { if (!isLight) { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.background = 'rgba(255,255,255,0.05)'; } }}
              autoComplete="current-password"
            />
            {error && <p className="text-red-400 text-sm">{error}</p>}
            {loading && (
              <div className="flex justify-center">
                <LottieAnim type="loading" size={48} />
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand hover:bg-brand/85 disabled:opacity-50 text-white rounded-lg py-2.5 font-medium text-sm shimmer-hover transition-all duration-300 mt-1"
              style={{ boxShadow: '0 4px 20px rgba(224,0,94,0.3)' }}
            >
              {loading ? '확인 중...' : '직원 로그인'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs mt-5" style={{ color: 'rgba(255,255,255,0.5)' }}>
          Pink Paper 관리자 전용
        </p>
      </div>
    </div>
  );
}
